/**
 * Camada de erros padronizados da aplicação.
 * - Fornece um tipo único (AppError) com code, title e metadata
 * - Fábricas para normalização de erros vindos de API, Corel/WebView e rede
 * - Compatível com o sistema de notificações (NotificationProvider)
 */

export type ErrorSeverity = 'critical' | 'error' | 'warning' | 'info';

export enum ErrorCode {
  UNKNOWN = 'UNKNOWN',
  // Launcher/Corel
  LAUNCHER_NOT_READY = 'LAUNCHER_NOT_READY',
  // Corel/WebView
  WEBVIEW_UNAVAILABLE = 'WEBVIEW_UNAVAILABLE',
  COREL_OPEN_FAILED = 'COREL_OPEN_FAILED',
  FILE_NOT_FOUND = 'FILE_NOT_FOUND',
  HASH_MISMATCH = 'HASH_MISMATCH',
  SMB_UNAUTHORIZED = 'SMB_UNAUTHORIZED',
  PATH_INACCESSIBLE = 'PATH_INACCESSIBLE',
  OPEN_TIMEOUT = 'OPEN_TIMEOUT',
  // WebView2 action response codes (padronizados)
  ENTRADA_INVALIDA = 'entrada_invalida',
  DOCUMENTO_INATIVO = 'documento_inativo',
  FORMATO_NAO_SUPORTADO = 'formato_nao_suportado',
  PATH_INVALIDO = 'path_invalido',
  PATH_SISTEMA_NEGADO = 'path_sistema_negado',
  PATH_NAO_AUTORIZADO = 'path_nao_autorizado',
  CRIAR_DIRETORIO_FALHOU = 'criar_diretorio_falhou',
  ARQUIVO_JA_EXISTE = 'arquivo_ja_existe',
  SMB_CREDENCIAIS_AUSENTES = 'smb_credenciais_ausentes',
  SMB_AUTENTICACAO_FALHOU = 'smb_autenticacao_falhou',
  SMB_CONEXAO_FALHOU = 'smb_conexao_falhou',
  IO_ACESSO_NEGADO = 'io_acesso_negado',
  IO_DISCO_CHEIO = 'io_disco_cheio',
  TIMEOUT = 'timeout',
  INTEGRIDADE_DIVERGENTE = 'integridade_divergente',
  USUARIO_CANCELOU = 'usuario_cancelou',
  ITEM_INVALIDO = 'item_invalido',
  TARGETS_OBSOLETOS = 'targets_obsoletos',
  // API
  API_UNAVAILABLE = 'API_UNAVAILABLE',
  API_TIMEOUT = 'API_TIMEOUT',
  API_4XX = 'API_4XX',
  API_5XX = 'API_5XX',
  // API specific business codes
  DIMENSION_LIMIT_EXCEEDED = 'DIMENSION_LIMIT_EXCEEDED',
  METRAGEM_EXCEEDED = 'METRAGEM_EXCEEDED',
  WAW_CONFLICT = 'WAW_CONFLICT',
  MD5_CONFLICT = 'MD5_CONFLICT',
  FK_ORCAMENTO_NOT_FOUND = 'FK_ORCAMENTO_NOT_FOUND',
  FK_OIT_NOT_FOUND = 'FK_OIT_NOT_FOUND',
  FK_PRODUTO_NOT_FOUND = 'FK_PRODUTO_NOT_FOUND',
  FK_PRODUTO_RULE_NOT_FOUND = 'FK_PRODUTO_RULE_NOT_FOUND',
  LAYOUT_ITEM_NOT_FOUND = 'LAYOUT_ITEM_NOT_FOUND',
  // Validação
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  // Exportação
  EXPORT_REGISTER_FAILED = 'EXPORT_REGISTER_FAILED',
  EXPORT_STAGE_UPDATE_FAILED = 'EXPORT_STAGE_UPDATE_FAILED',
  EXPORT_LAYOUT_FAILED = 'EXPORT_LAYOUT_FAILED',
  EXPORT_LAYOUT_REGISTER_FAILED = 'EXPORT_LAYOUT_REGISTER_FAILED',
  EXPORT_MISSING_OST_ID = 'EXPORT_MISSING_OST_ID',
  EXPORT_MISSING_USER_ID = 'EXPORT_MISSING_USER_ID',
  // Tools API specific errors
  TOOL_NOT_FOUND = 'TOOL_NOT_FOUND',
  COMMAND_NOT_FOUND = 'COMMAND_NOT_FOUND',
  CLIENTS_NOT_FOUND = 'CLIENTS_NOT_FOUND',
  PRODUCT_NOT_FOUND = 'PRODUCT_NOT_FOUND',
  QUALITIES_NOT_FOUND = 'QUALITIES_NOT_FOUND',
  RULES_NOT_FOUND = 'RULES_NOT_FOUND',
  CONFIG_NOT_FOUND = 'CONFIG_NOT_FOUND',
  NO_SERIAL_AVAILABLE = 'NO_SERIAL_AVAILABLE',
  TENANT_REQUIRED = 'TENANT_REQUIRED',
  BAD_GATEWAY = 'BAD_GATEWAY',
  GATEWAY_TIMEOUT = 'GATEWAY_TIMEOUT',
  NO_DISCOUNT_FOUND = 'NO_DISCOUNT_FOUND',
  CATEGORIA_ERROR = 'CATEGORIA_ERROR',
  REGRAS_ERROR = 'REGRAS_ERROR',
  // Oneprint API errors - Autenticação (401)
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  USER_ID_REQUIRED = 'USER_ID_REQUIRED',
  // Oneprint API errors - Validação (400)
  MISSING_REQUIRED_FIELDS = 'MISSING_REQUIRED_FIELDS',
  INVALID_IMAGES = 'INVALID_IMAGES',
  INVALID_MD5 = 'INVALID_MD5',
  INVALID_PAYLOAD = 'INVALID_PAYLOAD',
  INVALID_KEY = 'INVALID_KEY',
  INVALID_PERM_ID = 'INVALID_PERM_ID',
  INVALID_BODY = 'INVALID_BODY',
  INVALID_QUERY = 'INVALID_QUERY',
  MISSING_PARAMETERS = 'MISSING_PARAMETERS',
  EMPTY_UPDATES = 'EMPTY_UPDATES',
  // Oneprint API errors - Conflito (409)
  PEDIDO_JA_SALVO = 'PEDIDO_JA_SALVO',
  // Oneprint API errors - Tenant (400)
  TENANT_INVALID = 'TENANT_INVALID',
  // Oneprint API errors - Autorização (403)
  UNAUTHORIZED_403 = 'UNAUTHORIZED_403',
  // Oneprint API errors - Não encontrado (404)
  ITEM_NOT_FOUND = 'ITEM_NOT_FOUND',
  ORCAMENTO_NOT_FOUND = 'ORCAMENTO_NOT_FOUND',
  // Oneprint API errors - Servidor (500)
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  UNEXPECTED_ERROR = 'UNEXPECTED_ERROR',
  UNSPECIFIED = 'UNSPECIFIED',
  ER_BAD_FIELD_ERROR = 'ER_BAD_FIELD_ERROR',
  // Erros Prisma
  P2002 = 'P2002',
  P2003 = 'P2003',
  P2021 = 'P2021',
}

export interface AppErrorOptions {
  code?: ErrorCode;
  title?: string;
  details?: string;
  severity?: ErrorSeverity;
  metadata?: Record<string, any>;
  cause?: unknown;
}

/** Erro padronizado da aplicação */
export class AppError extends Error {
  public code: ErrorCode;
  public title?: string;
  public details?: string;
  public severity: ErrorSeverity;
  public metadata?: Record<string, any>;
  public cause?: unknown;

  constructor(message: string, opts: AppErrorOptions = {}) {
    super(message);
    this.name = 'AppError';
    this.code = opts.code ?? ErrorCode.UNKNOWN;
    this.title = opts.title;
    this.details = opts.details;
    this.severity = opts.severity ?? 'error';
    this.metadata = opts.metadata;
    this.cause = opts.cause;
  }
}

/** Normaliza erros vindos da API */
export function fromApi(err: any, fallback?: Partial<AppErrorOptions>): AppError {
  try {
    const status = err?.status ?? err?.response?.status;
    const data = err?.data ?? err?.response?.data;
    const msg = data?.message || data?.error || err?.message || 'Erro de API';

    const code = (() => {
      if (status >= 500) return ErrorCode.API_5XX;
      if (status >= 400) return ErrorCode.API_4XX;
      return ErrorCode.API_UNAVAILABLE;
    })();

    return new AppError(msg, {
      code,
      severity: status >= 500 ? 'critical' : 'error',
      metadata: { status, data },
      ...fallback,
    });
  } catch {
    return new AppError('Erro de API', { code: ErrorCode.API_UNAVAILABLE, ...fallback });
  }
}

/** Normaliza erros do Corel/WebView */
export function fromCorel(err: any, fallback?: Partial<AppErrorOptions>): AppError {
  // Detectar padrões comuns
  const msg = String(err?.message || err || 'Falha ao comunicar com o Corel/WebView');
  let code = ErrorCode.COREL_OPEN_FAILED;
  if (/webview|not initialized|dispon/i.test(msg)) code = ErrorCode.WEBVIEW_UNAVAILABLE;
  if (/file.*not.*found|não encontrado/i.test(msg)) code = ErrorCode.FILE_NOT_FOUND;
  if (/hash/i.test(msg)) code = ErrorCode.HASH_MISMATCH;
  if (/timeout/i.test(msg)) code = ErrorCode.OPEN_TIMEOUT;
  return new AppError(msg, { code, severity: 'error', ...fallback, metadata: { raw: err, ...(fallback?.metadata || {}) } });
}

/** Normaliza erros genéricos de rede */
export function fromNetwork(err: any, fallback?: Partial<AppErrorOptions>): AppError {
  const isTimeout = /timeout|tempo esgotado/i.test(String(err?.message || ''));
  return new AppError(isTimeout ? 'Tempo esgotado' : 'Falha de rede', {
    code: isTimeout ? ErrorCode.API_TIMEOUT : ErrorCode.API_UNAVAILABLE,
    severity: isTimeout ? 'warning' : 'error',
    metadata: { raw: err },
    ...fallback,
  });
}

/** Helper para envolver promessas e converter exceções em AppError */
export async function handleAsync<T>(fn: () => Promise<T>, onError?: (e: AppError) => void): Promise<T | undefined> {
  try {
    return await fn();
  } catch (e: any) {
    const appErr = e instanceof AppError ? e : new AppError(e?.message || 'Operação falhou', { cause: e });
    try { onError?.(appErr); } catch {}
    return undefined;
  }
}

/** Converte resposta padronizada de action (WebView2) em AppError */
export function fromActionError(resp: any, fallback?: Partial<AppErrorOptions>): AppError {
  const codeString = String(resp?.code || '').trim();
  const msg = String(resp?.message || resp?.result?.mensagem || 'Falha na ação').trim();
  const requestId = resp?.requestId;

  // Mapear severidade padrão por código
  const codeToSeverity: Partial<Record<ErrorCode, ErrorSeverity>> = {
    [ErrorCode.TIMEOUT]: 'warning',
    [ErrorCode.INTEGRIDADE_DIVERGENTE]: 'warning',
    [ErrorCode.USUARIO_CANCELOU]: 'info',
    [ErrorCode.ARQUIVO_JA_EXISTE]: 'warning',
    [ErrorCode.IO_DISCO_CHEIO]: 'error',
    [ErrorCode.IO_ACESSO_NEGADO]: 'error',
    [ErrorCode.SMB_AUTENTICACAO_FALHOU]: 'error',
    [ErrorCode.SMB_CONEXAO_FALHOU]: 'error',
    [ErrorCode.SMB_CREDENCIAIS_AUSENTES]: 'warning',
    [ErrorCode.PATH_NAO_AUTORIZADO]: 'error',
    [ErrorCode.PATH_SISTEMA_NEGADO]: 'error',
    [ErrorCode.PATH_INVALIDO]: 'error',
    [ErrorCode.ENTRADA_INVALIDA]: 'warning',
    [ErrorCode.DOCUMENTO_INATIVO]: 'error',
    [ErrorCode.FORMATO_NAO_SUPORTADO]: 'error',
    [ErrorCode.ITEM_INVALIDO]: 'error',
    [ErrorCode.TARGETS_OBSOLETOS]: 'warning',
  };

  const stringToEnum = (s: string): ErrorCode => {
    const known = new Set(Object.values(ErrorCode));
    return (known.has(s as ErrorCode) ? (s as ErrorCode) : ErrorCode.UNKNOWN);
  };

  const code = stringToEnum(codeString);
  const severity = codeToSeverity[code] ?? 'error';

  return new AppError(msg || 'Falha na ação', {
    code,
    severity,
    metadata: { requestId, raw: resp },
    ...fallback,
  });
}

/**
 * Centraliza tratamento de erros de API, mapeando status codes e códigos específicos
 * para mensagens padronizadas do ErrorCatalog.
 * 
 * @param error - APIError da requisição (deve ter status, data e message)
 * @returns AppError padronizado com mensagem do ErrorCatalog quando disponível
 */
export function handleApiError(error: { status: number; data?: any; message: string }): AppError {
  // Primeiro, normalizar usando fromApi
  const baseError = fromApi(error);
  
  // Tentar extrair código específico de error.data.code
  const specificCode = error.data?.code || error.data?.error?.code;
  
  if (specificCode) {
    // Tentar mapear código específico para ErrorCode
    const stringToEnum = (s: string): ErrorCode | null => {
      const known = new Set(Object.values(ErrorCode));
      return (known.has(s as ErrorCode) ? (s as ErrorCode) : null);
    };
    
    const mappedCode = stringToEnum(String(specificCode));
    if (mappedCode) {
      // Buscar mensagem no ErrorCatalog
      try {
        const { ErrorCatalog } = require('../components/feedback/ErrorCatalog');
        const catalogEntry = ErrorCatalog[mappedCode];
        
        if (catalogEntry) {
          const message = typeof catalogEntry.message === 'function' 
            ? catalogEntry.message(error.data) 
            : catalogEntry.message;
          
          return new AppError(message, {
            code: mappedCode,
            title: catalogEntry.title,
            severity: catalogEntry.severity || baseError.severity,
            metadata: { ...baseError.metadata, specificCode },
          });
        }
      } catch {
        // Se ErrorCatalog não disponível, continuar com fallback
      }
    }
  }
  
  // Mapear status codes específicos para mensagens mais descritivas
  if (error.status === 400) {
    const errs = error.data?.errors;
    if (Array.isArray(errs) && errs.length > 0) {
      const list = errs.map((x: any) => 
        typeof x === 'string' ? x : (x?.message || JSON.stringify(x))
      ).join('; ');
      return new AppError(`Erros de validação: ${list}`, {
        code: ErrorCode.VALIDATION_ERROR,
        severity: 'error',
        metadata: baseError.metadata,
      });
    }
  }
  
  if (error.status === 401) {
    const code = String(error.data?.code || '').trim();
    const msg = code === 'USER_ID_REQUIRED' 
      ? 'Token inválido. Faça login novamente.' 
      : 'Não autorizado. Faça login novamente.';
    return new AppError(msg, {
      code: ErrorCode.API_4XX,
      severity: 'error',
      metadata: baseError.metadata,
    });
  }
  
  if (error.status === 422) {
    const code = String(error.data?.code || '').trim();
    let msg = 'IDs inválidos. Verifique orc_id, oit_id e pro_id.';
    if (code === 'FK_ORCAMENTO_NOT_FOUND') msg = 'Orçamento não encontrado.';
    else if (code === 'FK_OIT_NOT_FOUND') msg = 'Item do orçamento não encontrado.';
    else if (code === 'FK_PRODUTO_NOT_FOUND') msg = 'Produto não encontrado (pro_id).';
    else if (code === 'FK_PRODUTO_RULE_NOT_FOUND') msg = 'Regra ativa do produto não encontrada.';
    else if (code === 'DIMENSION_LIMIT_EXCEEDED') msg = 'Existem objetos fora do limite de dimensões permitido.';
    else if (code === 'METRAGEM_EXCEEDED') msg = 'A metragem/quantidade dos objetos excede o limite do pedido.';
    
    return new AppError(msg, {
      code: code === 'FK_ORCAMENTO_NOT_FOUND' ? ErrorCode.FK_ORCAMENTO_NOT_FOUND :
            code === 'FK_OIT_NOT_FOUND' ? ErrorCode.FK_OIT_NOT_FOUND :
            code === 'FK_PRODUTO_NOT_FOUND' ? ErrorCode.FK_PRODUTO_NOT_FOUND :
            code === 'FK_PRODUTO_RULE_NOT_FOUND' ? ErrorCode.FK_PRODUTO_RULE_NOT_FOUND :
            code === 'DIMENSION_LIMIT_EXCEEDED' ? ErrorCode.DIMENSION_LIMIT_EXCEEDED :
            code === 'METRAGEM_EXCEEDED' ? ErrorCode.METRAGEM_EXCEEDED :
            ErrorCode.VALIDATION_ERROR,
      severity: 'error',
      metadata: baseError.metadata,
    });
  }
  
  if (error.status === 500) {
    return new AppError('Erro inesperado no servidor. Tente novamente em instantes.', {
      code: ErrorCode.API_5XX,
      severity: 'critical',
      metadata: baseError.metadata,
    });
  }
  
  // Fallback para erro base normalizado
  return baseError;
}


